import gql from 'graphql-tag';

/**
 * Editorial AI assistant. The API invokes Amazon Bedrock server-side and
 * executes the model's tool calls as the requesting user, so the browser
 * never sees AWS credentials and the assistant is bound by the editor's own
 * permissions.
 */
export const AI_ASSISTANT_MESSAGE = gql`
  mutation AIAssistantMessage($message: Str!, $articleId: Str, $articleIds: [Str], $history: [AIAssistantTurnInput]) {
    aiAssistantMessage(input: { message: $message, articleId: $articleId, articleIds: $articleIds, history: $history }) {
      content
      reasoning
      toolsUsed
      links {
        text
        url
      }
      created {
        kind
        oid
        title
        url
        published
        sources {
          text
          url
        }
      }
      searchResults {
        id
        oid
        contentType
        title
        canonicalUrl
        imageUrl
        postAt
        score
        matchReason
        source
        authorNames
        taxonomyNames
        keywordNames
      }
      success
      errors
    }
  }
`;

/**
 * Associate articles with an article as related content. Same mutation the
 * Related tab issues (see RELATED_MUTATION in graphql/relatedPanel), spelled
 * out here so the AI panel's action is codegen-typed.
 */
export const ARTICLE_ADD_RELATED_ARTICLES = gql`
  mutation ArticleAddRelatedArticles($articleId: IDType!, $relatedArticleIds: [IDType!]) {
    articleAddRelatedArticles(
      input: { articleId: $articleId, relatedArticleIds: $relatedArticleIds }
    ) {
      relatedArticles {
        id
        oid
        title
      }
      errors
      success
    }
  }
`;

/**
 * AI semantic search - resolved by the API's aiSemanticSearch resolver which
 * proxies to the MCP server for hybrid (SOLR keyword + Qdrant vector) search.
 * limit is capped at 50 server-side.
 */
export const SEMANTIC_SEARCH = gql`
  query SemanticSearch(
    $query: Str!
    $taxonomyIds: [IDType]
    $authorNames: [Str]
    $dateFrom: Str
    $dateTo: Str
    $contentTypes: [Str]
    $limit: Integer
  ) {
    aiSemanticSearch(
      query: $query
      taxonomyIds: $taxonomyIds
      authorNames: $authorNames
      dateFrom: $dateFrom
      dateTo: $dateTo
      contentTypes: $contentTypes
      limit: $limit
    ) {
      totalCount
      query
      results {
        id
        oid
        contentType
        title
        canonicalUrl
        imageUrl
        postAt
        score
        matchReason
        source
        authorNames
        taxonomyNames
        taxonomyIds
        keywordNames
      }
    }
  }
`;

/**
 * Related content suggestions - analyzes an article (by id, or the supplied
 * text) and returns semantically related content via the MCP server's
 * ai_find_related_content tool. limit is capped at 30 server-side.
 */
export const RELATED_CONTENT_SUGGESTIONS = gql`
  query RelatedContentSuggestions(
    $articleId: IDType!
    $articleText: Str
    $contentTypes: [Str]
    $limit: Integer
  ) {
    aiRelatedContentSuggestions(
      articleId: $articleId
      articleText: $articleText
      contentTypes: $contentTypes
      limit: $limit
    ) {
      totalCount
      results {
        id
        oid
        contentType
        title
        canonicalUrl
        imageUrl
        postAt
        score
        matchReason
        source
        authorNames
        taxonomyNames
        taxonomyIds
        keywordNames
      }
    }
  }
`;

/**
 * The chat's welcome-state buttons, configured on the Content Lifecycle page.
 * Separate from contentLifecycleConfig so the chat reads only the prompts, not
 * the model and policy text around them.
 */
export const AI_ASSISTANT_QUICK_PROMPTS = gql`
  query AIAssistantQuickPrompts($pageType: Str) {
    aiAssistantQuickPrompts(pageType: $pageType) {
      label
      prompt
    }
  }
`;
